import React, { FC, useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { connect } from 'react-redux';

import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Box from "@mui/material/Box";

import { FormInput, InputEvent } from 'components/FormInput'
import { PartnerContext, Steps } from 'contexts/PartnerContext';
import { ApplicationState } from 'store';
import { actionCreators } from 'store/CompanyPartnership';
import getNumbersFormString from 'shared/functions/getNumbersFormString';
import { AddPartnerRequestDto } from 'api/models';
import analytics from 'services/analytics';
import {AnalyticsEvents} from "../../../shared/constants/analytics";

type FormValues = {
  code: string;
};

interface ReduxProps {
  partnerPhone: string,
  codeError: string,
  createCompanyPartnership: (data: AddPartnerRequestDto, callBack: () => void) => void,
  removePartnerErrors: () => void,
}

const Verification: FC<ReduxProps> = props => {
  const { partnerPhone, codeError } = props;
  const { setStep, onCloseModal } = useContext(PartnerContext);

  useEffect(() => {
    if (codeError) {
      methods.setError('code', { message: codeError })
    }
    props.removePartnerErrors();
  }, [codeError]);

  const onSubmit = (data: FormValues) => {
    props.createCompanyPartnership(
      {
        code: data.code,
        partnerPhone,
      },
      () => onCloseModal());
    analytics().sendEvent(AnalyticsEvents.business_structure_modal_add_partner_code_verification);
  };

  const onChange = ({ target }: InputEvent) => {
    methods.setValue('code', getNumbersFormString(target.value).substring(0, 4));
  };

  const methods = useForm<FormValues>({
    mode: 'onChange'
  });

  return (
    <>
      <DialogContent dividers>
        <Typography
          onClick={() => setStep(Steps.phoneNumber)}
          fontWeight={300}
          mb={3}
          sx={{ cursor: 'pointer' }}
          color={({ colors }) => colors.body}
        >
          На номер мобільного телефону {partnerPhone} був надісланий код підтвердження.
          <Link ml={1} fontWeight={500} underline='none' mb={3}>Змінити номер телефону</Link>
        </Typography>
        <FormProvider {...methods}>
          <Typography
            ml={2}
            mb={.5}
            color={({ colors }) => colors.secondaryGrey}
            fontSize='0.8571428571428571rem'>
            ВВЕДІТЬ КОД ПІДТВЕРДЖЕННЯ:
          </Typography>
          <FormInput
            placeholder='4 цифри із СМС'
            fullWidth
            name='code'
            rules={{
              minLength: {
                value: 4,
                message: ''
              },
              onChange,
            }}
            autoFocus={true}
          />
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Box marginLeft='auto'>
          <Button onClick={() => {
            onCloseModal();
            analytics().sendEvent(AnalyticsEvents.business_structure_modal_add_partner_code_verification_cancel);
          }} size='large' variant='contained' color='inherit'>
            Відмінити
          </Button>
          <Button
            sx={{ ml: 2 }}
            onClick={methods.handleSubmit(onSubmit)}
            disabled={
              !methods.getValues('code') ||
              !!Object.keys(methods.formState.errors).length
            }
            size='large'
            variant='contained'
          >
            Додати
          </Button>
        </Box>
      </DialogActions>
    </>
  );
};

const mapStateToProps = ({ companyPartnership: { partner, errors } }: ApplicationState) => ({
  partnerPhone: partner.ownerPhone,
  codeError: errors.code
});

const mapDispatchToProps = {
  createCompanyPartnership: actionCreators.createCompanyPartnership,
  removePartnerErrors: actionCreators.removePartnerErrors,
};

export default connect(mapStateToProps, mapDispatchToProps)(Verification);
